import { DocumentNode } from 'graphql';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import { GetOrderQuery as GetOrder } from '__generated__/graphql';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import DeleteOrderMutation from './DeleteOrderMutation';
import GetOrderQuery from './GetOrderQuery';
import GetParticipantOrdersQuery from './GetParticipantOrdersQuery';
import UI from '../../../../common/UI';
import useMutation from '../../../../api/useMutation';

type Order = GetOrder['order'];

export interface DeleteOrderFormProps {
  order: Order;
  refetchQueries: DocumentNode[];
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const DeleteOrderForm = ({
  order, onSuccess, onCancel, refetchQueries = [GetParticipantOrdersQuery, GetOrderQuery],
}: DeleteOrderFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();

  const [deleteOrder, { loading }] = useMutation(
    DeleteOrderMutation,
    {
      variables: {
        id: order.id,
      },
      refetchQueries,
      awaitRefetchQueries: true,
      onCompleted: () => {
        onSuccess?.();
        notifier.success(t('notifications.order_deleted'));
      },
    },
  );

  return (
    <UI.Form onSubmit={() => deleteOrder()}>
      <UI.FormGrid>
        <UI.H4>
          {t('delete_order')}
        </UI.H4>
        <UI.Paragraph>
          {t('delete_order_description')}
        </UI.Paragraph>
        <UI.HR />
        <UI.Div sc={{ textAlign: 'right' }}>
          {onCancel && (
            <UI.Button onClick={() => onCancel()} disabled={loading}>
              {t('cancel')}
            </UI.Button>
          )}
          {' '}
          <UI.Button
            type="submit"
            sc={{ brand: 'secondary' }}
            disabled={loading}
          >
            {t('delete_order')}
          </UI.Button>
        </UI.Div>
      </UI.FormGrid>
    </UI.Form>
  );
};
interface UseDeleteOrderModalProps {
  order?: Order;
  refetchQueries?: DocumentNode[];
}

const useDeleteOrderModal = ({ order, refetchQueries }: UseDeleteOrderModalProps) => {
  const [openModal, closeModal] = useModal(
    () => (
      <UI.Modal close={closeModal}>
        {!order && <UI.PageLoader />}
        {order && (
          <DeleteOrderForm
            order={order}
            refetchQueries={refetchQueries}
            onSuccess={closeModal}
            onCancel={closeModal}
          />
        )}
      </UI.Modal>
    ),
    [order],
  );

  return [openModal, closeModal];
};

export default useDeleteOrderModal;
